/*
 * Lizenz-Reporting — die Kundenansicht auf das, was monatlich nach oben
 * gemeldet wird (Telemetrie-Konzept §8) plus der Zustellstand (§10).
 *
 * WARUM DIESE ANSICHT SO GEBAUT IST
 *
 * §8.2, letzter Satz: „kein verstecktes Telemetriemeldeverhalten". Das ist
 * keine Nettigkeit, sondern die Bedingung, unter der das Feature vertretbar
 * ist. Deshalb zwei Regeln, die diese Datei einhält:
 *
 *   1. Die Tabelle wird aus `payloadJson` gerechnet — der Zeichenfolge, über
 *      die die Signatur gebildet und die als Body gesendet wurde. Es gibt
 *      keine zweite, hübschere Projektion vom Server, die hier gerendert
 *      würde: was der Kunde sieht, IST der Bericht. Darunter steht er
 *      zusätzlich im Rohtext, mit Signatur, zum Kopieren.
 *   2. Lesen braucht kein `license.admin`. Wer sich auf diesem Server
 *      anmelden kann, darf sehen, was über die Installation gemeldet wird.
 *      Erzeugen und Senden verändern etwas und bleiben rechtegebunden.
 *
 * Der Zustellstand kommt aus derselben Zeile, die der Sender schreibt — ein
 * fehlgeschlagener Versand steht hier, nicht nur im Log (§10).
 */

import { useMemo, useState } from 'preact/hooks';
import { api, ApiError } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { showToast } from '../components/Toaster';

// -----------------------------------------------------------------------------
// Wire-Formen. Absichtlich lokal in dieser Datei und nicht in api/types.ts:
// die Reporting-Ansicht ist eine geschlossene Einheit, und api/types.ts ist
// eine Datei, an der mehrere Arbeitsstränge gleichzeitig hängen.
// -----------------------------------------------------------------------------

/** Zustellstand — spiegelt `LicenseReportDeliveryState` wortgleich. */
export type DeliveryState = 'Pending' | 'Failed' | 'Delivered' | 'Blocked' | 'GaveUp';

export interface SnapshotSummary {
  id: string;
  period: string;
  generatedAt: string;
  deliveryState: DeliveryState;
  attemptCount: number;
  lastAttemptAt: string | null;
  nextAttemptAt: string | null;
  deliveredAt: string | null;
  retryDeadline: string | null;
  lastStatusCode: number | null;
  lastError: string | null;
}

export interface ReportingStatus {
  enabled: boolean;
  instanceId: string | null;
  customerKey: string;
  endpoint: string;
  /** Nur ob — der Wert verlässt den Server nie. */
  instanceSecretConfigured: boolean;
  timeZone: string;
  dueAtLocalHour: number;
  retryWindowDays: number;
  blockReason: string | null;
  snapshots: SnapshotSummary[];
}

export interface SnapshotDetail {
  summary: SnapshotSummary;
  /** Der Bericht, exakt wie signiert und gesendet. */
  payloadJson: string;
  payloadSignature: string;
}

/** Eine Modellzeile im Bericht — spiegelt `LicenseUsageReportModelLine`. */
interface ReportModelLine {
  modelKey: string;
  modelName: string;
  assignedPlants: number;
  activePlants: number;
  activeControllers: number;
  recordedDatapoints: number;
  plantsAdded: number;
  plantsRemoved: number;
  plantsArchived: number;
  plantsSwitchedAway: number;
  netGrowth: number;
  growthBase: number | null;
  growthRatePercent: number | null;
}

/** Der Bericht — spiegelt `LicenseUsageReport`. */
interface Report {
  schemaVersion: number;
  instanceId: string;
  customerKey: string;
  period: string;
  periodStart: string;
  periodEnd: string;
  timeZone: string;
  generatedAt: string;
  generationLagHours: number;
  activePlantsBasis: 'prompt' | 'upper-bound' | string;
  previousPeriod: string | null;
  models: ReportModelLine[];
}

const NUM = new Intl.NumberFormat('de-AT');

// -----------------------------------------------------------------------------
// Panel
// -----------------------------------------------------------------------------

export function LicenseReportingSection({ canAdmin }: { canAdmin: boolean }) {
  const [refreshTick, setRefreshTick] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const refresh = () => setRefreshTick((n) => n + 1);

  const statusState = useAsync(
    (signal) => api.get<ReportingStatus>('/api/licensing/reporting/status', signal),
    [refreshTick],
  );

  const status = statusState.status === 'ok' ? statusState.data : null;
  const snapshots = status?.snapshots ?? [];

  // Ohne ausdrückliche Wahl steht der jüngste Bericht offen — das ist der,
  // um den es beim Nachsehen fast immer geht.
  const activeId = selectedId ?? snapshots[0]?.id ?? null;

  const detailState = useAsync(
    (signal) =>
      activeId
        ? api.get<SnapshotDetail>(`/api/licensing/reporting/snapshots/${activeId}`, signal)
        : Promise.resolve<SnapshotDetail | null>(null),
    [activeId, refreshTick],
  );

  const detail = detailState.status === 'ok' ? detailState.data : null;

  const generate = async () => {
    setBusy(true);
    try {
      const created = await api.post<SnapshotDetail>('/api/licensing/reporting/snapshots', {
        period: null,
      });
      setSelectedId(created.summary.id);
      showToast({
        tone: 'success',
        title: `Bericht ${created.summary.period}`,
        message: 'Erzeugt und lokal abgelegt.',
        autoHideAfterMs: 2500,
      });
      refresh();
    } catch (e) {
      showToast({
        tone: 'error',
        title: 'Bericht nicht erzeugt',
        message: errorMessage(e, 'Erzeugung fehlgeschlagen.'),
        autoHideAfterMs: 0,
      });
    } finally {
      setBusy(false);
    }
  };

  const send = async (id: string) => {
    setBusy(true);
    try {
      const res = await api.post<{ outcome: string; snapshot: SnapshotSummary }>(
        `/api/licensing/reporting/snapshots/${id}/send`,
      );
      const ok = res.outcome === 'Delivered';
      showToast({
        tone: ok ? 'success' : 'warn',
        title: ok ? 'Bericht zugestellt' : 'Versand nicht erfolgreich',
        // Die Server-Meldung nennt den Grund und den nächsten Versuch — die
        // darf nicht wegblinken, wenn es schiefging.
        message: ok ? res.snapshot.period : res.snapshot.lastError ?? res.outcome,
        autoHideAfterMs: ok ? 2500 : 0,
      });
      refresh();
    } catch (e) {
      showToast({
        tone: 'error',
        title: 'Versand fehlgeschlagen',
        message: errorMessage(e, 'Der Versand konnte nicht angestoßen werden.'),
        autoHideAfterMs: 0,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section class="hv-license__panel">
      <div class="hv-license__panel-title">
        <span>Lizenz-Reporting</span>
        {canAdmin && (
          <button
            type="button"
            class="hv-btn hv-btn--sm"
            disabled={busy || !status?.enabled}
            title={
              status?.enabled
                ? 'Erzeugt den Bericht für den Vormonat, falls er noch fehlt'
                : 'Das Lizenz-Reporting ist abgeschaltet (LicenseReporting:Enabled=false)'
            }
            onClick={() => void generate()}
          >
            Bericht für Vormonat erzeugen
          </button>
        )}
      </div>

      <p class="hv-license__panel-note">
        Einmal im Monat meldet dieser Server, wie viele Anlagen mit welchem Lizenzmodell
        aktiv verwaltet werden. Hier steht der <strong>exakt gemeldete Inhalt</strong> —
        keine Zusammenfassung davon — und ob er angekommen ist. Anlagennamen, Adressen,
        Messwerte oder Anlagen-Ids sind nicht Teil der Meldung.
      </p>

      {statusState.status === 'loading' && (
        <div class="hv-license__empty">Lade Reporting-Status…</div>
      )}
      {statusState.status === 'error' && (
        <div class="hv-license__empty">Konnte den Reporting-Status nicht laden.</div>
      )}

      {status && (
        <>
          <ConfigSummary status={status} />

          {snapshots.length === 0 ? (
            <div class="hv-license__empty">
              Es wurde noch kein Bericht erzeugt. Der Hintergrundjob legt ihn am 1. des
              Monats um {String(status.dueAtLocalHour).padStart(2, '0')}:00 ({status.timeZone})
              für den Vormonat an.
            </div>
          ) : (
            <div class="hv-report__periods">
              {snapshots.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  class={
                    'hv-report__period' + (s.id === activeId ? ' hv-report__period--active' : '')
                  }
                  onClick={() => setSelectedId(s.id)}
                >
                  <span class="hv-report__period-key">{s.period}</span>
                  <StateBadge state={s.deliveryState} />
                </button>
              ))}
            </div>
          )}

          {detailState.status === 'loading' && activeId && (
            <div class="hv-license__empty">Lade Bericht…</div>
          )}
          {detail && (
            <SnapshotView
              detail={detail}
              canAdmin={canAdmin}
              busy={busy}
              onSend={() => void send(detail.summary.id)}
            />
          )}
        </>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Konfigurationslage
// -----------------------------------------------------------------------------

function ConfigSummary({ status }: { status: ReportingStatus }) {
  return (
    <>
      {!status.enabled && (
        <div class="hv-report__banner hv-report__banner--muted">
          Das Lizenz-Reporting ist abgeschaltet. Es wird nichts erzeugt und nichts gesendet.
        </div>
      )}
      {/* Fehlendes Secret ist der Fall, der nicht still bleiben darf: der
          Bericht entsteht, aber er geht nicht raus. */}
      {status.enabled && status.blockReason && (
        <div class="hv-report__banner hv-report__banner--warn">{status.blockReason}</div>
      )}

      <dl class="hv-report__config">
        <div>
          <dt>Instanz</dt>
          <dd>
            <code>{status.instanceId ?? 'nicht konfiguriert'}</code>
          </dd>
        </div>
        <div>
          <dt>Kundenschlüssel</dt>
          <dd>{status.customerKey || '—'}</dd>
        </div>
        <div>
          <dt>Meldet an</dt>
          <dd>{status.endpoint ? <code>{status.endpoint}</code> : 'nicht konfiguriert'}</dd>
        </div>
        <div>
          <dt>Instanz-Secret</dt>
          <dd>
            {status.instanceSecretConfigured ? (
              <span class="hv-license__badge hv-license__badge--ok">vorhanden</span>
            ) : (
              <span class="hv-license__badge hv-license__badge--warn">fehlt</span>
            )}
          </dd>
        </div>
        <div>
          <dt>Fällig</dt>
          <dd>
            1. des Monats, {String(status.dueAtLocalHour).padStart(2, '0')}:00 ({status.timeZone})
          </dd>
        </div>
        <div>
          <dt>Wiederholung</dt>
          <dd>bis zu {status.retryWindowDays} Tage</dd>
        </div>
      </dl>
    </>
  );
}

// -----------------------------------------------------------------------------
// Ein Bericht
// -----------------------------------------------------------------------------

function SnapshotView({
  detail,
  canAdmin,
  busy,
  onSend,
}: {
  detail: SnapshotDetail;
  canAdmin: boolean;
  busy: boolean;
  onSend(): void;
}) {
  // Der Bericht wird aus der gesendeten Zeichenfolge gelesen, nicht aus einer
  // Server-Projektion. Ein Parse-Fehler wird angezeigt statt verschluckt —
  // dann stimmt etwas mit der Ablage nicht, und das ist eine Information.
  const parsed = useMemo<{ report: Report | null; error: string | null }>(() => {
    try {
      return { report: JSON.parse(detail.payloadJson) as Report, error: null };
    } catch (e) {
      return { report: null, error: e instanceof Error ? e.message : 'unlesbar' };
    }
  }, [detail.payloadJson]);

  const s = detail.summary;
  const report = parsed.report;

  return (
    <div class="hv-report__snapshot">
      <div class="hv-report__delivery">
        <StateBadge state={s.deliveryState} />
        <span class="hv-license__muted">
          erzeugt {formatDateTime(s.generatedAt)}
          {s.attemptCount > 0 && ` · ${s.attemptCount} Versuch${s.attemptCount === 1 ? '' : 'e'}`}
          {s.deliveredAt && ` · zugestellt ${formatDateTime(s.deliveredAt)}`}
          {s.nextAttemptAt && ` · nächster Versuch ${formatDateTime(s.nextAttemptAt)}`}
          {!s.deliveredAt && s.retryDeadline && ` · Fenster bis ${formatDateTime(s.retryDeadline)}`}
        </span>
        <span class="hv-license__spacer" />
        {canAdmin && s.deliveryState !== 'Delivered' && (
          <button type="button" class="hv-btn hv-btn--sm" disabled={busy} onClick={onSend}>
            Jetzt senden
          </button>
        )}
      </div>

      {s.lastError && (
        <div class="hv-report__banner hv-report__banner--warn">
          {s.lastStatusCode !== null && <strong>HTTP {s.lastStatusCode}: </strong>}
          {s.lastError}
        </div>
      )}

      {parsed.error && (
        <div class="hv-report__banner hv-report__banner--warn">
          Der abgelegte Berichtstext ist nicht lesbar ({parsed.error}). Unten steht er im
          Rohtext.
        </div>
      )}

      {report && (
        <>
          {/* Die ehrliche Fußnote zur Aktivitätszahl. Sie steht hier und
              nicht im Kleingedruckten, weil „aktiv verwaltet" rückwirkend
              nicht messbar ist: clients.last_seen_at kennt nur den letzten
              Kontakt, keine Historie. */}
          {report.activePlantsBasis === 'prompt' ? (
            <p class="hv-license__panel-note">
              Der Bericht wurde {formatHours(report.generationLagHours)} nach Monatsende
              erzeugt. „Aktiv verwaltet" (mindestens ein Regler verbunden in den letzten 30
              Tagen) ist damit der <strong>Stand zum Monatsende</strong>.
            </p>
          ) : (
            <div class="hv-report__banner hv-report__banner--muted">
              Dieser Bericht wurde erst {formatHours(report.generationLagHours)} nach
              Monatsende erzeugt. „Aktiv verwaltet" ist deshalb eine{' '}
              <strong>Obergrenze</strong> und keine Messung: es wird nur der letzte Kontakt
              jedes Reglers gespeichert, keine Historie — eine Anlage, die im Berichtsmonat
              still war und danach wieder auftauchte, ist nicht mehr unterscheidbar.
            </div>
          )}

          <div class="hv-license__table-scroll">
            <table class="hv-license__table">
              <thead>
                <tr>
                  <th>Lizenzmodell</th>
                  <th class="hv-license__num">Anlagen</th>
                  <th class="hv-license__num">aktiv</th>
                  <th class="hv-license__num">Regler</th>
                  <th class="hv-license__num">Datenpunkte</th>
                  <th class="hv-license__num">Zugang</th>
                  <th class="hv-license__num">Abgang</th>
                  <th class="hv-license__num">netto</th>
                  <th class="hv-license__num">vs. Vormonat</th>
                </tr>
              </thead>
              <tbody>
                {report.models.map((m) => (
                  <tr key={m.modelKey}>
                    <td>
                      {m.modelName} <span class="hv-license__muted">({m.modelKey})</span>
                    </td>
                    <td class="hv-license__num">{NUM.format(m.assignedPlants)}</td>
                    <td class="hv-license__num">{NUM.format(m.activePlants)}</td>
                    <td class="hv-license__num">{NUM.format(m.activeControllers)}</td>
                    <td class="hv-license__num">{NUM.format(m.recordedDatapoints)}</td>
                    <td class="hv-license__num">{m.plantsAdded === 0 ? '—' : `+${m.plantsAdded}`}</td>
                    <td class="hv-license__num" title={removalTitle(m)}>
                      {m.plantsRemoved === 0 ? '—' : `−${m.plantsRemoved}`}
                    </td>
                    <td class="hv-license__num">
                      {m.netGrowth > 0 ? `+${m.netGrowth}` : NUM.format(m.netGrowth)}
                    </td>
                    <td class="hv-license__num">
                      {m.growthRatePercent === null ? (
                        <span
                          class="hv-license__muted"
                          title={
                            report.previousPeriod === null
                              ? 'Kein Vormonatsbericht vorhanden — ohne Vergleichswert gibt es keine Rate.'
                              : 'Der Vormonat hatte an diesem Modell keine Anlagen.'
                          }
                        >
                          —
                        </span>
                      ) : (
                        <span title={`Basis: ${m.growthBase} Anlagen im Vormonat`}>
                          {m.growthRatePercent > 0 ? '+' : ''}
                          {NUM.format(m.growthRatePercent)} %
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p class="hv-license__panel-note">
            Berichtsmonat {report.period} ({report.timeZone}), Formatversion{' '}
            {report.schemaVersion}
            {report.previousPeriod === null
              ? ' · kein Vormonat vorhanden'
              : ` · Vergleich mit ${report.previousPeriod}`}
            . „Abgang" umfasst archivierte Anlagen und Wechsel auf ein anderes Modell; ein
            Wechsel ist nur bestimmbar, wenn der Vormonatsbericht vorliegt.
          </p>
        </>
      )}

      <details class="hv-report__raw">
        <summary>Gemeldeter Text im Original (signiert und so gesendet)</summary>
        <p class="hv-license__panel-note">
          Signatur (HMAC-SHA256 über genau diese Bytes):{' '}
          <code>{detail.payloadSignature || 'nicht signiert — kein Instanz-Secret'}</code>
        </p>
        <pre>{detail.payloadJson}</pre>
      </details>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Kleinteile
// -----------------------------------------------------------------------------

function StateBadge({ state }: { state: DeliveryState }) {
  switch (state) {
    case 'Delivered':
      return <span class="hv-license__badge hv-license__badge--ok">zugestellt</span>;
    case 'Pending':
      return <span class="hv-license__badge">wartet auf Versand</span>;
    case 'Failed':
      return <span class="hv-license__badge hv-license__badge--warn">Versand offen</span>;
    case 'GaveUp':
      return <span class="hv-license__badge hv-license__badge--warn">nicht zugestellt</span>;
    case 'Blocked':
      return <span class="hv-license__badge hv-license__badge--warn">nicht versandt</span>;
    default:
      return <span class="hv-license__badge hv-license__badge--muted">{state}</span>;
  }
}

function removalTitle(m: ReportModelLine): string {
  return `${m.plantsArchived} archiviert, ${m.plantsSwitchedAway} auf ein anderes Modell gewechselt`;
}

function formatHours(hours: number): string {
  if (hours < 48) {
    const rounded = Math.round(hours * 10) / 10;
    return `${NUM.format(rounded)} Stunden`;
  }
  return `${NUM.format(Math.round(hours / 24))} Tage`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-AT', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; detail?: string; title?: string } | undefined;
    return body?.error ?? body?.detail ?? body?.title ?? `HTTP ${err.status} — ${fallback}`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
